import { template as _$template } from "solid-js/web";
import { delegateEvents as _$delegateEvents } from "solid-js/web";
import { setAttribute as _$setAttribute } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { use as _$use } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<p class=preferences-overlay__status>OPENING PREFERENCES…`),
  _tmpl$2 = /*#__PURE__*/_$template(`<p class="preferences-overlay__status preferences-overlay__status--error">The baked assets could not be loaded.`),
  _tmpl$3 = /*#__PURE__*/_$template(`<div class=preferences-overlay role=dialog aria-label=Preferences data-testid=preferences-overlay><canvas class=preferences-overlay__canvas>`);
import { createEffect, createSignal, onCleanup, onMount, Show } from "solid-js";
import { browserSettingsStore } from "../audio/settings.ts";
import { createPreferencesSurface } from "../shell/preferences-surface.ts";
import { createScreenRuntime } from "../shell/screen-runtime.ts";
import { normalizeKey, PREFERENCES_KEY } from "./keys.ts";
/**
 * The PREFERENCES panel over a flight (`O`), drawn as one of the shell's own
 * 320×200 screens — the same controller, the same widgets and the same painter
 * the `/settings` route uses (`shell/preferences-surface.ts`), so the panel a
 * pilot meets in the cockpit is the panel he met in the menus.
 *
 * ## It swallows the keyboard, on purpose
 *
 * `docs/engineering/lessons.md` (2026-08-25) records the shape of this bug: a
 * piece of chrome over the flight view that the input layer does not know about
 * lets input through to the viewport, and the viewport's primary control is the
 * **trigger**. The pointer half is settled by the root class being in
 * `keys.ts`'s `UI_OVERLAY_CLASSES` (`isViewportPointerTarget` walks out from
 * the target and takes the first answer, so every control inside this panel is
 * chrome). The keyboard half is settled here: the listener below sits on the
 * **capture** phase at `window`, and while the panel is open it stops the event
 * dead. `keyboard.ts`'s listener is a bubble-phase listener on the same window,
 * so it is never reached — no key that navigates this panel can also fly the
 * aeroplane, and the `pressed` set cannot silently fill up behind it.
 *
 * `keyup` is deliberately *not* swallowed: a key held when the panel opened
 * still has to release, or the aeroplane resumes with a stuck input. Nor is a
 * key held with a modifier — a browser shortcut stays the browser's.
 *
 * **A joystick needs a third mechanism**, because it has no events to swallow:
 * its buttons are a level, polled every frame. The route passes `blocked` to
 * `flight/gamepad.ts`, which reports an empty frame while this panel is up and
 * then keeps every button that was down suppressed until it is released — the
 * same thing the capture-phase listener above achieves for a key, arrived at
 * the other way round.
 */
function PreferencesPanel(props) {
  const store = browserSettingsStore();
  const surface = createPreferencesSurface({
    store,
    sinks: props.sinks,
    ...(props.gamepad ? {
      gamepad: props.gamepad
    } : {}),
    onClose: () => {
      props.onClose();
      return undefined;
    },
    inFlight: true
  });
  // No title theme: this panel opens over a sortie that is already making
  // noise (`screen-runtime.ts`'s `ScreenRuntimeOptions.music`).
  const runtime = createScreenRuntime(surface, () => {}, {
    music: false
  });
  const [status, setStatus] = createSignal(runtime.status());
  runtime.subscribe(setStatus);
  let canvas;
  function onKeyDown(event) {
    if (!props.open) return;
    // A browser shortcut is still the browser's — only the bare keys belong to
    // the panel, exactly as `keyboard.ts` decides for the aeroplane.
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    event.stopPropagation();
    event.preventDefault();
    // `O` shuts the panel from wherever it is, because it is the panel's own
    // key and a toggle. `Escape` is *not* handled here: it goes to the runtime,
    // which backs out one screen — off the Realism Panel onto PREFERENCES, and
    // only then out to the flight (`preferences-surface.ts`'s `pref:accept`).
    if (normalizeKey(event.key) === PREFERENCES_KEY) {
      props.onClose();
      return;
    }
    runtime.keyDown(event);
  }
  onMount(() => {
    runtime.mount(canvas);
    const observer = new ResizeObserver(() => runtime.refresh());
    if (canvas) observer.observe(canvas);
    window.addEventListener("keydown", onKeyDown, {
      capture: true
    });
    onCleanup(() => {
      observer.disconnect();
      window.removeEventListener("keydown", onKeyDown, {
        capture: true
      });
      runtime.dispose();
    });
  });

  // Every opening re-reads storage and returns to the front screen: a setting
  // can have moved while the panel was shut (`V` mutes without it), and the
  // panel must not show — and then write back — a stale value.
  createEffect(() => {
    if (!props.open) return;
    surface.reopen();
    runtime.refresh();
  });
  return (() => {
    var _el$ = _tmpl$3(),
      _el$2 = _el$.firstChild;
    _el$2.$$pointermove = event => runtime.pointerMove(event);
    _el$2.$$pointerdown = event => runtime.pointerDown(event);
    _$use(element => canvas = element, _el$2);
    _$insert(_el$, _$createComponent(Show, {
      get when() {
        return status() === "loading";
      },
      get children() {
        return _tmpl$();
      }
    }), null);
    _$insert(_el$, _$createComponent(Show, {
      get when() {
        return status() === "failed";
      },
      get children() {
        return _tmpl$2();
      }
    }), null);
    _$effect(_p$ => {
      var _v$ = !props.open,
        _v$2 = !props.open;
      _v$ !== _p$.e && _el$.classList.toggle("preferences-overlay--hidden", _p$.e = _v$);
      _v$2 !== _p$.t && _$setAttribute(_el$, "aria-hidden", _p$.t = _v$2);
      return _p$;
    }, {
      e: undefined,
      t: undefined
    });
    return _el$;
  })();
}

/**
 * The panel as a flying surface mounts it: nothing at all until the player has
 * pressed `O` once, then a permanently mounted panel that hides itself.
 *
 * The lazy mount is `MapOverlay.tsx`'s, for its reason — the screen decodes the
 * shell's fonts, palette and two sprite sheets on its first paint, and a sortie
 * nobody opens the panel on should not pay for any of it.
 */
export function FlightPreferences(props) {
  const [everOpened, setEverOpened] = createSignal(false);
  createEffect(() => {
    if (props.open) setEverOpened(true);
  });
  return _$createComponent(Show, {
    get when() {
      return everOpened();
    },
    get children() {
      return _$createComponent(PreferencesPanel, {
        get open() {
          return props.open;
        },
        get sinks() {
          return props.sinks;
        },
        get onClose() {
          return props.onClose;
        },
        get gamepad() {
          return props.gamepad;
        }
      });
    }
  });
}
_$delegateEvents(["pointerdown", "pointermove"]);